import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    Alert,
    Skeleton,
    Avatar,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Star as PriorityIcon,
    Gavel as ActionIcon,
    History as HistoryIcon,
    Block as BanIcon,
    PauseCircleOutline as SuspendIcon,
    WarningAmberRounded as WarnIcon,
    CheckCircleOutline as DismissIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi, ReportDetail, DashboardStats } from '../../api/adminApi';
import AdminSidebar from './components/AdminSidebar';
import UserActionModal from './components/UserActionModal';
import UserProfileView from './components/UserProfileView';
import EngagementPanel from './components/EngagementPanel';
import { adminTokens as T } from '../../theme/adminTokens';

const PRIORITY_TONE: Record<string, { dot: string; bg: string; text: string; label: string }> = {
    urgent: { dot: T.color.error.base,   bg: T.color.error.soft,   text: T.color.error.text,   label: 'Urgent' },
    high:   { dot: T.color.warning.base, bg: T.color.warning.soft, text: T.color.warning.text, label: 'High' },
    medium: { dot: T.color.info.base,    bg: T.color.info.soft,    text: T.color.info.text,    label: 'Medium' },
    low:    { dot: T.color.success.base, bg: T.color.success.soft, text: T.color.success.text, label: 'Low' },
};
const STATUS_TONE: Record<string, { bg: string; text: string; label: string }> = {
    pending:   { bg: T.color.warning.soft, text: T.color.warning.text, label: 'Pending' },
    reviewing: { bg: T.color.info.soft,    text: T.color.info.text,    label: 'Reviewing' },
    resolved:  { bg: T.color.success.soft, text: T.color.success.text, label: 'Resolved' },
    dismissed: { bg: T.color.surface.sunken, text: T.color.text.secondary, label: 'Dismissed' },
};

const formatReportType = (type: string) =>
    type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const initialsFrom = (name?: string | null, email?: string | null) => {
    const source = (name && name.trim()) || (email ? email.split('@')[0] : '');
    if (!source) return '?';
    const parts = source.split(/[._\s-]/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || source[0].toUpperCase();
};

const formatTimeAgo = (dateString: string) => {
    const diffInSeconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
};

const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
    const t = PRIORITY_TONE[priority?.toLowerCase()] || PRIORITY_TONE.medium;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`,
            px: `${T.space[2]}px`, py: `2px`,
            borderRadius: `${T.radius.pill}px`,
            bgcolor: t.bg, color: t.text,
            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.dot }} />
            {t.label}
        </Box>
    );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const t = STATUS_TONE[status?.toLowerCase()] || STATUS_TONE.pending;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: `${T.space[2]}px`, py: `2px`,
            borderRadius: `${T.radius.pill}px`,
            bgcolor: t.bg, color: t.text,
            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
        }}>{t.label}</Box>
    );
};

// Shared card primitive matching the rest of the admin section
const Panel: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
    <Box sx={{
        bgcolor: T.color.surface.raised,
        border: `1px solid ${T.color.surface.border}`,
        borderRadius: `${T.radius.lg}px`,
        boxShadow: T.shadow.sm,
        p: `${T.space[5]}px`,
        ...sx,
    }}>
        {children}
    </Box>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{
        fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
        color: T.color.text.tertiary, textTransform: 'uppercase', letterSpacing: 0.5,
        mb: `${T.space[2]}px`,
    }}>{children}</Typography>
);

const PersonRow: React.FC<{
    name?: string | null;
    email?: string | null;
    tone: 'info' | 'error';
    children?: React.ReactNode;
    action?: React.ReactNode;
}> = ({ name, email, tone, children, action }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[3]}px` }}>
        <Avatar sx={{
            width: 40, height: 40,
            bgcolor: tone === 'error' ? T.color.error.soft : T.color.info.soft,
            color: tone === 'error' ? T.color.error.text : T.color.info.text,
            fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold,
        }}>
            {name || email ? initialsFrom(name, email) : <PersonIcon fontSize="small" />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary }}>
                {name || 'Unknown'}
            </Typography>
            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {email || '—'}
            </Typography>
            {children}
        </Box>
        {action}
    </Box>
);

const ReportDetailScreen: React.FC = () => {
    const navigate = useNavigate();
    const { reportId } = useParams<{ reportId: string }>();

    const [report, setReport] = useState<ReportDetail | null>(null);
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Action modal state
    const [actionModalOpen, setActionModalOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState<string>('');

    // Profile view state
    const [profileViewOpen, setProfileViewOpen] = useState(false);

    // Warning status state
    const [userHasActiveWarning, setUserHasActiveWarning] = useState(false);
    const [checkingWarningStatus, setCheckingWarningStatus] = useState(false);

    useEffect(() => {
        if (reportId) {
            fetchReportDetails();
        }
    }, [reportId]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await adminApi.getDashboardStats();
                if (active) setStats(r.data);
            } catch {
                if (active) setStats(null);
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (report?.reported_user_email) {
            checkUserWarningStatus();
        }
    }, [report?.reported_user_email]);

    const fetchReportDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await adminApi.getReportDetail(reportId!);
            setReport(response.data);

        } catch (err: any) {
            setError(err.message || 'Failed to load report details');
        } finally {
            setLoading(false);
        }
    };

    const checkUserWarningStatus = async () => {
        if (!report?.reported_user_email || report.reported_user_email === 'Unknown') {
            setUserHasActiveWarning(false);
            return;
        }

        try {
            setCheckingWarningStatus(true);
            const warningStatus = await adminApi.checkUserWarningStatus(report.reported_user_email);
            setUserHasActiveWarning(warningStatus.hasActiveWarning);
        } catch (err: any) {
            setUserHasActiveWarning(false);
        } finally {
            setCheckingWarningStatus(false);
        }
    };

    const handleActionSubmit = async (actionType: string, reason: string, duration?: number) => {
        try {
            const response = await adminApi.takeAdminAction({
                reportId: reportId!,
                actionType: actionType as 'dismiss' | 'warn' | 'suspend' | 'ban',
                reason,
                duration,
                reportedUserEmail: report?.reported_user_email!
            });

            // Refresh data
            await fetchReportDetails();

            if (response?.data?.enforcementStatus === 'database_only') {
                setError(response.message || 'The profile was disabled, but its authentication restriction needs review.');
            }

            setActionModalOpen(false);
            setSelectedAction('');

        } catch (err: any) {
            throw err; // Re-throw to let the modal handle the error
        }
    };

    const handleClearWarning = async () => {
        if (!report?.reported_user_email || report.reported_user_email === 'Unknown') {
            setError('Cannot clear warning: No valid user email found');
            return;
        }

        try {
            const reason = 'Warning cleared from admin report review';
            await adminApi.clearUserWarning({
                reportedUserEmail: report.reported_user_email,
                reason
            });

            // Refresh data
            await Promise.all([
                fetchReportDetails(),
                checkUserWarningStatus()
            ]);

        } catch (err: any) {
            setError(`Failed to clear warning: ${err.message}`);
        }
    };

    const isPending = (report?.status || 'pending') === 'pending';

    const pageShell = (children: React.ReactNode) => (
        <Box sx={{ display: 'flex', bgcolor: T.color.surface.page, minHeight: '100vh' }}>
            <AdminSidebar pendingReportsCount={stats?.pendingReports ?? 0} />
            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: `${T.space[4]}px`, md: `${T.space[6]}px` },
                minWidth: 0,
            }}>
                {children}
            </Box>
        </Box>
    );

    if (loading) {
        return pageShell(
            <>
                <Skeleton variant="text" width={260} height={44} sx={{ mb: `${T.space[1]}px` }} />
                <Skeleton variant="text" width={360} height={24} sx={{ mb: `${T.space[6]}px` }} />
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                        <Skeleton variant="rounded" height={320} sx={{ borderRadius: `${T.radius.lg}px`, mb: `${T.space[3]}px` }} />
                        <Skeleton variant="rounded" height={180} sx={{ borderRadius: `${T.radius.lg}px` }} />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Skeleton variant="rounded" height={420} sx={{ borderRadius: `${T.radius.lg}px` }} />
                    </Grid>
                </Grid>
            </>
        );
    }

    if (error || !report) {
        return pageShell(
            <Alert
                severity="error"
                sx={{ maxWidth: 600, borderRadius: `${T.radius.md}px` }}
                action={
                    <Button color="inherit" size="small" onClick={() => navigate('/admin/reports')}>
                        Back to reports
                    </Button>
                }
            >
                {error || 'Report not found'}
            </Alert>
        );
    }

    return pageShell(
        <>
            {/* Page header */}
            <Box sx={{ mb: `${T.space[6]}px` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px`, mb: `${T.space[1]}px` }}>
                    <IconButton
                        onClick={() => navigate('/admin/reports')}
                        aria-label="Back to reports"
                        sx={{
                            color: T.color.text.secondary,
                            border: `1px solid ${T.color.surface.border}`,
                            bgcolor: T.color.surface.raised,
                            '&:hover': { bgcolor: T.color.surface.sunken, color: T.color.text.primary },
                        }}
                    >
                        <BackIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{
                        fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                        color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
                    }}>Report details</Typography>
                </Box>
                <Typography sx={{ fontSize: T.type.size.md, color: T.color.text.secondary, ml: '52px' }}>
                    Review report information and take moderation action.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Main Report Info */}
                <Grid item xs={12} lg={8}>
                    <Panel sx={{ mb: `${T.space[3]}px` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: `${T.space[3]}px`, mb: `${T.space[4]}px` }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: T.type.size.xl, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
                                    {formatReportType(report.report_type)}
                                </Typography>
                                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, fontVariantNumeric: 'tabular-nums', mt: `${T.space[1]}px` }}>
                                    Report ID: {report.id}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: `${T.space[2]}px`, flexShrink: 0 }}>
                                <PriorityBadge priority={report.priority || 'medium'} />
                                <StatusBadge status={report.status || 'pending'} />
                            </Box>
                        </Box>

                        <Box sx={{ height: '1px', bgcolor: T.color.surface.border, my: `${T.space[4]}px` }} />

                        {/* Report Content */}
                        {report.content && (
                            <Box sx={{ mb: `${T.space[5]}px` }}>
                                <SectionLabel>Additional details</SectionLabel>
                                <Typography sx={{
                                    fontSize: T.type.size.sm,
                                    color: T.color.text.primary,
                                    bgcolor: T.color.surface.sunken,
                                    p: `${T.space[3]}px`,
                                    borderRadius: `${T.radius.md}px`,
                                    fontStyle: 'italic',
                                }}>
                                    “{report.content}”
                                </Typography>
                            </Box>
                        )}

                        {/* Reporter Info */}
                        <Box sx={{ mb: `${T.space[5]}px` }}>
                            <SectionLabel>Reporter</SectionLabel>
                            <PersonRow
                                tone="info"
                                name={report.reporter_profile?.full_name || 'Anonymous'}
                                email={report.reporter_email}
                            />
                        </Box>

                        {/* Reported User Info */}
                        <Box>
                            <SectionLabel>Reported user</SectionLabel>
                            <PersonRow
                                tone="error"
                                name={report.reported_user_profile?.full_name || 'Unknown User'}
                                email={report.reported_user_email}
                                action={
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        endIcon={<ArrowForwardIcon fontSize="small" />}
                                        onClick={() => setProfileViewOpen(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: T.type.weight.semibold,
                                            borderColor: T.color.surface.borderStrong,
                                            color: T.color.text.primary,
                                            '&:hover': { borderColor: T.color.brand[400], bgcolor: T.color.brand[50] },
                                        }}
                                    >
                                        View profile
                                    </Button>
                                }
                            >
                                <Box sx={{ display: 'flex', gap: `${T.space[1]}px`, mt: `${T.space[2]}px`, flexWrap: 'wrap' }}>
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center',
                                        px: `${T.space[2]}px`, py: `2px`,
                                        borderRadius: `${T.radius.pill}px`,
                                        bgcolor: report.total_reports_against_user > 3 ? T.color.error.soft : T.color.surface.sunken,
                                        color: report.total_reports_against_user > 3 ? T.color.error.text : T.color.text.secondary,
                                        fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                    }}>
                                        {report.total_reports_against_user} report{report.total_reports_against_user === 1 ? '' : 's'}
                                    </Box>
                                    {userHasActiveWarning && (
                                        <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`,
                                            px: `${T.space[2]}px`, py: `2px`,
                                            borderRadius: `${T.radius.pill}px`,
                                            bgcolor: T.color.warning.soft, color: T.color.warning.text,
                                            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
                                        }}>
                                            <WarnIcon sx={{ fontSize: 14 }} /> Active warning
                                        </Box>
                                    )}
                                </Box>
                            </PersonRow>
                        </Box>
                    </Panel>

                    {/* Engagement metrics for the reported user */}
                    <EngagementPanel engagement={report.engagement} sx={{ mb: `${T.space[3]}px` }} />

                    {/* Admin Actions History */}
                    <Panel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px`, mb: `${T.space[4]}px` }}>
                            <HistoryIcon sx={{ color: T.color.text.tertiary, fontSize: 20 }} />
                            <Typography sx={{ fontSize: T.type.size.lg, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
                                Action history
                            </Typography>
                        </Box>

                        {(!report?.adminActions || report.adminActions.length === 0) ? (
                            <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.tertiary, textAlign: 'center', py: `${T.space[5]}px` }}>
                                No actions taken yet
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                                {(report?.adminActions || []).map((action) => (
                                    <Box
                                        key={action.id}
                                        sx={{
                                            p: `${T.space[3]}px`,
                                            bgcolor: T.color.surface.sunken,
                                            borderRadius: `${T.radius.md}px`,
                                            borderLeft: `3px solid ${T.color.brand[400]}`,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: `${T.space[2]}px`, mb: `${T.space[1]}px` }}>
                                            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary }}>
                                                {action.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </Typography>
                                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, whiteSpace: 'nowrap' }}>
                                                {formatTimeAgo(action.created_at)}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, mb: `${T.space[1]}px` }}>
                                            By {action.admin_email}
                                        </Typography>
                                        <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.primary }}>
                                            {action.reason}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Panel>
                </Grid>

                {/* Actions Panel */}
                <Grid item xs={12} lg={4}>
                    <Panel sx={{ position: { lg: 'sticky' }, top: { lg: `${T.space[6]}px` } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px`, mb: `${T.space[4]}px` }}>
                            <ActionIcon sx={{ color: T.color.brand[500], fontSize: 20 }} />
                            <Typography sx={{ fontSize: T.type.size.lg, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
                                Take action
                            </Typography>
                        </Box>

                        {!isPending && (
                            <Box sx={{
                                mb: `${T.space[3]}px`, p: `${T.space[3]}px`,
                                bgcolor: T.color.surface.sunken, borderRadius: `${T.radius.md}px`,
                            }}>
                                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary }}>
                                    This report is <strong>{(report.status || 'resolved').toLowerCase()}</strong>. Moderation actions are disabled.
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<DismissIcon />}
                                onClick={() => { setSelectedAction('dismiss'); setActionModalOpen(true); }}
                                disabled={!isPending}
                                sx={{
                                    justifyContent: 'flex-start', textTransform: 'none', fontWeight: T.type.weight.semibold,
                                    borderColor: T.color.surface.borderStrong, color: T.color.text.primary, py: `${T.space[2]}px`,
                                    '&:hover': { borderColor: T.color.brand[400], bgcolor: T.color.brand[50] },
                                }}
                            >
                                Dismiss report
                            </Button>

                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<WarnIcon />}
                                onClick={() => { setSelectedAction('warn'); setActionModalOpen(true); }}
                                disabled={!isPending}
                                sx={{
                                    justifyContent: 'flex-start', textTransform: 'none', fontWeight: T.type.weight.semibold,
                                    borderColor: T.color.warning.base, color: T.color.warning.text, py: `${T.space[2]}px`,
                                    '&:hover': { borderColor: T.color.warning.base, bgcolor: T.color.warning.soft },
                                }}
                            >
                                Issue warning
                            </Button>

                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<SuspendIcon />}
                                onClick={() => { setSelectedAction('suspend'); setActionModalOpen(true); }}
                                disabled={!isPending}
                                sx={{
                                    justifyContent: 'flex-start', textTransform: 'none', fontWeight: T.type.weight.semibold,
                                    borderColor: T.color.error.base, color: T.color.error.text, py: `${T.space[2]}px`,
                                    '&:hover': { borderColor: T.color.error.base, bgcolor: T.color.error.soft },
                                }}
                            >
                                Suspend user
                            </Button>

                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<BanIcon />}
                                onClick={() => { setSelectedAction('ban'); setActionModalOpen(true); }}
                                disabled={!isPending}
                                sx={{
                                    justifyContent: 'flex-start', textTransform: 'none', fontWeight: T.type.weight.semibold,
                                    bgcolor: T.color.error.base, color: '#fff', py: `${T.space[2]}px`, boxShadow: 'none',
                                    '&:hover': { bgcolor: T.color.error.text, boxShadow: 'none' },
                                }}
                            >
                                Ban user
                            </Button>

                            {userHasActiveWarning && (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleClearWarning}
                                    disabled={checkingWarningStatus}
                                    sx={{
                                        mt: `${T.space[1]}px`, justifyContent: 'flex-start', textTransform: 'none',
                                        fontWeight: T.type.weight.semibold,
                                        borderColor: T.color.success.base, color: T.color.success.text, py: `${T.space[2]}px`,
                                        '&:hover': { borderColor: T.color.success.base, bgcolor: T.color.success.soft },
                                    }}
                                >
                                    {checkingWarningStatus ? 'Checking…' : 'Clear warning'}
                                </Button>
                            )}
                        </Box>

                        <Box sx={{ height: '1px', bgcolor: T.color.surface.border, my: `${T.space[4]}px` }} />

                        {/* Report Metadata */}
                        <SectionLabel>Report information</SectionLabel>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                            {[
                                { label: 'Submitted', value: new Date(report.created_at).toLocaleDateString() },
                                { label: 'Category', value: formatReportType(report.report_type) },
                                { label: 'Total reports', value: String(report.total_reports_against_user) },
                            ].map((row) => (
                                <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: `${T.space[3]}px` }}>
                                    <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary }}>
                                        {row.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.primary, fontWeight: T.type.weight.medium, textAlign: 'right' }}>
                                        {row.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Panel>
                </Grid>
            </Grid>

            {/* Action Modal */}
            <UserActionModal
                open={actionModalOpen}
                onClose={() => {
                    setActionModalOpen(false);
                    setSelectedAction('');
                }}
                onSubmit={handleActionSubmit}
                actionType={selectedAction}
                userEmail={report?.reported_user_email || ''}
                reportType={report?.report_type || ''}
                reportContent={report?.content || undefined}
            />

            {/* User Profile View */}
            <UserProfileView
                open={profileViewOpen}
                onClose={() => setProfileViewOpen(false)}
                userEmail={report?.reported_user_email || ''}
            />
        </>
    );
};

export default ReportDetailScreen;
